import type { BorderEdge, LeafPrimitive, RegionBorders, Zone, ZoneComponent, ZoneStyle } from '@tsagko/label-contracts'
import { splitChildBoxes } from '@tsagko/label-zpl-pipeline'
import { clampSplitRatio } from '../block-configurator/dimension-ops'

function isLeaf(component: ZoneComponent | undefined): component is LeafPrimitive {
  return component !== undefined && component.type !== 'baked-block-instance'
}

export function findZoneByPath(root: Zone, path: string[]): Zone | undefined {
  if (path.length === 0) return root
  if (!root.children || !root.split) return undefined
  const [head, ...rest] = path
  const idx = Number(head)
  if (idx !== 0 && idx !== 1) return undefined
  return findZoneByPath(root.children[idx as 0 | 1], rest)
}

/**
 * Walk the zone tree from `root` down `path`, tracking the bounding box at
 * each split. Returns the box belonging to the zone AT `path`, or undefined
 * if the path cannot be resolved (runs into a leaf, or an out-of-range index).
 *
 * The root box is `{ x: 0, y: 0, w: <template/block width>, h: <height> }` —
 * the caller supplies it since this module has no notion of document dims.
 *
 * Shared by both routes: TemplateDesignerRoute uses it (via floor-ops) for the
 * block-floor-aware divider/ruler clamp; BlockConfiguratorRoute uses it
 * directly for the region-size (mm) editor's ratio conversion — a block's own
 * layout never contains baked instances, so it needs the box without any of
 * floor-ops' floor-clamping semantics.
 */
export function resolveBoxAtPath(
  zone: Zone,
  path: string[],
  box: { x: number; y: number; w: number; h: number },
): { x: number; y: number; w: number; h: number } | undefined {
  if (path.length === 0) return box
  if (!zone.children || !zone.split) return undefined
  const [head, ...rest] = path
  const idx = head === '0' ? 0 : head === '1' ? 1 : undefined
  if (idx === undefined) return undefined
  const [boxA, boxB] = splitChildBoxes(box, zone.split)
  const childBox = idx === 0 ? boxA : boxB
  return resolveBoxAtPath(zone.children[idx], rest, childBox)
}

export function findLeafComponent(
  root: Zone,
  path: string[]
): LeafPrimitive | undefined {
  const zone = findZoneByPath(root, path)
  if (!zone) return undefined
  return isLeaf(zone.component) ? zone.component : undefined
}

export function updateComponentAtPath(
  root: Zone,
  path: string[],
  updater: (current: LeafPrimitive) => LeafPrimitive
): Zone {
  if (path.length === 0) {
    if (!isLeaf(root.component)) return root
    return { ...root, component: updater(root.component) }
  }
  if (!root.children || !root.split) return root
  const [head, ...rest] = path
  const idx = Number(head)
  if (idx !== 0 && idx !== 1) return root
  const childIndex = idx as 0 | 1
  const nextChild = updateComponentAtPath(root.children[childIndex], rest, updater)
  const nextChildren: [Zone, Zone] =
    childIndex === 0 ? [nextChild, root.children[1]] : [root.children[0], nextChild]
  return { ...root, children: nextChildren }
}

export function replaceComponentAtPath(
  root: Zone,
  path: string[],
  next: LeafPrimitive
): Zone {
  return updateComponentAtPath(root, path, () => next)
}

/**
 * Returns the `id` of the Zone at `path`, or `undefined` if the path is
 * invalid for the tree. An empty path returns the root's id.
 *
 * Paths use the canvas's index-based convention: `'0'` = first child,
 * `'1'` = second child.
 */
export function zoneIdAtPath(root: Zone, path: string[]): string | undefined {
  if (path.length === 0) return root.id
  if (!root.children || !root.split) return undefined
  const [head, ...rest] = path
  const idx = Number(head)
  if (idx !== 0 && idx !== 1) return undefined
  return zoneIdAtPath(root.children[idx as 0 | 1], rest)
}

/**
 * Returns a new Zone tree where the Zone at `path` is replaced with
 * `replacement`. If `path` is empty the root itself is replaced, except its
 * `id` — the host root's persisted `id` is preserved so callers that
 * generate a fresh id for the replacement (e.g. a new baked-block-instance
 * id) don't clobber the tree's root id when the replacement happens to land
 * at the root path (#137). If the path is invalid for the tree, returns the
 * input unchanged.
 *
 * Paths use the canvas's index-based convention: `'0'` = first child,
 * `'1'` = second child.
 */
export function replaceZoneAtPath(root: Zone, path: string[], replacement: Zone): Zone {
  if (path.length === 0) return { ...replacement, id: root.id }
  return replaceZoneAtPathDescend(root, path, replacement)
}

// Recursive worker for replaceZoneAtPath. Unlike the exported function, its
// own path.length === 0 base case fires once the path has been consumed down
// to the target node (at any depth) — that target is replaced verbatim,
// using `replacement`'s own id. Only the outer call's empty-path case means
// "this is the document root," which is where the id-preservation applies.
function replaceZoneAtPathDescend(root: Zone, path: string[], replacement: Zone): Zone {
  if (path.length === 0) return replacement
  if (!root.children || !root.split) return root
  const [head, ...rest] = path
  const idx = Number(head)
  if (idx !== 0 && idx !== 1) return root
  const childIndex = idx as 0 | 1
  const next = replaceZoneAtPathDescend(root.children[childIndex], rest, replacement)
  const nextChildren: [Zone, Zone] =
    childIndex === 0 ? [next, root.children[1]] : [root.children[0], next]
  return { ...root, children: nextChildren }
}

/**
 * Returns a new Zone tree where the Zone at `path` has its `style` field
 * replaced with `next` (or removed when `next` is `undefined`). If the path
 * is invalid for the tree, returns the input unchanged.
 *
 * Paths use the canvas's index-based convention: `'0'` = first child,
 * `'1'` = second child. An empty path targets the root.
 */
export function setZoneStyleAtPath(
  root: Zone,
  path: string[],
  next: ZoneStyle | undefined
): Zone {
  if (path.length === 0) {
    if (next === undefined) {
      const { style: _removed, ...rest } = root
      return rest as Zone
    }
    return { ...root, style: next }
  }
  if (!root.children) return root
  const [head, ...rest] = path
  const idx = head === '0' ? 0 : head === '1' ? 1 : undefined
  if (idx === undefined) return root
  const updatedChild = setZoneStyleAtPath(root.children[idx], rest, next)
  const newChildren: [Zone, Zone] =
    idx === 0 ? [updatedChild, root.children[1]] : [root.children[0], updatedChild]
  return { ...root, children: newChildren }
}

/**
 * Returns a new Zone tree where the split at `path` has its ratio
 * replaced (clamped via `clampSplitRatio`). If `path` does not point to
 * a split zone, returns the input unchanged.
 *
 * Paths use the canvas's index-based convention: `'0'` = first child,
 * `'1'` = second child. An empty path targets the root.
 */
export function setSplitRatioAtPath(root: Zone, path: string[], ratio: number): Zone {
  if (path.length === 0) {
    if (!root.split || !root.children) return root
    return { ...root, split: { ...root.split, ratio: clampSplitRatio(ratio) } }
  }
  if (!root.children) return root
  const [head, ...rest] = path
  const idx = head === '0' ? 0 : head === '1' ? 1 : undefined
  if (idx === undefined) return root
  const updatedChild = setSplitRatioAtPath(root.children[idx], rest, ratio)
  const newChildren: [Zone, Zone] = idx === 0
    ? [updatedChild, root.children[1]]
    : [root.children[0], updatedChild]
  return { ...root, children: newChildren }
}

/**
 * Returns a new Zone tree where the Zone at `path` has one border `edge`
 * set to `value` (or removed when `value` is `undefined`). When the last
 * remaining edge is removed, the `borders` key is dropped entirely so the
 * zone serializes without an empty object. Invalid paths return the input
 * unchanged. Index-based path convention ('0' / '1'); empty path = root.
 */
export function setZoneBordersAtPath(
  root: Zone,
  path: string[],
  edge: keyof RegionBorders,
  value: BorderEdge | undefined
): Zone {
  if (path.length === 0) {
    const next: RegionBorders = { ...root.borders }
    if (value === undefined) delete next[edge]
    else next[edge] = value
    if (!next.top && !next.right && !next.bottom && !next.left) {
      const { borders: _removed, ...rest } = root
      return rest as Zone
    }
    return { ...root, borders: next }
  }
  if (!root.children) return root
  const [head, ...rest] = path
  const idx = head === '0' ? 0 : head === '1' ? 1 : undefined
  if (idx === undefined) return root
  const updatedChild = setZoneBordersAtPath(root.children[idx], rest, edge, value)
  const newChildren: [Zone, Zone] =
    idx === 0 ? [updatedChild, root.children[1]] : [root.children[0], updatedChild]
  return { ...root, children: newChildren }
}
